// Route Your Career
// Detailed country content used by /country/:code
//
// Existing CountryDetail.jsx currently uses:
// - visa
// - lifestyle
//
// Additional fields below will be used in the next SEO upgrade:
// - seo
// - overview
// - highlights
// - eligibility
// - academics
// - universities
// - admissionSteps
// - documents
// - costOfLiving
// - nmc
// - faqs


export const countryDetails = {

  /* =========================================================
     GEORGIA — MBBS
  ========================================================= */

  ge: {

    seo: {
      title:
        'MBBS in Georgia for Indian Students 2026 | Fees & Admission',

      description:
        'Explore MBBS in Georgia for Indian students in 2026. Learn about fees, eligibility, universities, admission process, visa, student life and medical education.',

      keywords: [
        'MBBS in Georgia',
        'MBBS in Georgia for Indian students',
        'MBBS Georgia fees',
        'Georgia medical universities',
        'study MBBS abroad',
        'MBBS abroad for Indian students',
        'Georgia MBBS admission 2026'
      ]
    },


    overview: {
      title:
        'MBBS in Georgia for Indian Students',

      intro:
        'Georgia is a popular MBBS-abroad destination for Indian students looking for English-medium medical education, an international student environment and universities offering medical programmes at a comparatively accessible cost.',

      secondParagraph:
        'Students considering Georgia should compare universities carefully based on curriculum, clinical exposure, tuition fees, recognition status, location, teaching hospital access and the licensing requirements applicable when returning to India or practising in another country.'
    },


    highlights: [
      'English-medium medical programmes available',
      'International student environment',
      'Six-year medical education pathway at many universities',
      'Universities located mainly in Tbilisi and other major cities',
      'Direct admission pathways subject to university eligibility',
      'Indian student community in major university locations'
    ],


    eligibility: {
      title:
        'Eligibility for MBBS in Georgia',

      points: [
        'Student should have completed Class 12 or equivalent with Physics, Chemistry and Biology.',
        'NEET qualification is important for Indian students intending to remain eligible under applicable Indian medical education regulations.',
        'Minimum academic requirements differ between universities.',
        'A valid passport is required for international admission and visa processing.',
        'Students should independently verify current NMC and foreign medical graduate requirements before confirming admission.'
      ]
    },


    academics: {
      duration:
        'Generally 6 years',

      medium:
        'English',

      intake:
        'Commonly Fall / September intake',

      degree:
        'Medical Doctor / equivalent undergraduate medical programme depending on university',

      notes:
        'Programme structure, clinical rotations, internship format and academic calendar vary between universities.'
    },


    universities: [
      'Alte University',
      'Caucasus International University',
      'Caucasus University',
      'University of Georgia'
    ],


    fees: {
      range:
        'Approx. ₹20–28 lakh total tuition range across selected options',

      note:
        'Actual university tuition, hostel, insurance, registration, visa, living and other expenses vary by university and academic year. Students should rely on the latest official university fee letter before payment.'
    },


    admissionSteps: [
      'Initial counselling and university shortlisting',
      'Eligibility and academic document review',
      'Submit university application',
      'Receive admission / offer documentation',
      'Complete required university payments',
      'Prepare visa and supporting documents',
      'Complete travel arrangements',
      'University registration and orientation after arrival'
    ],


    documents: [
      'Valid passport',
      'Class 10 marksheet / certificate',
      'Class 12 marksheet / certificate',
      'NEET scorecard or qualification documents where applicable',
      'Passport-size photographs',
      'University application documents',
      'Medical documents when required',
      'Financial or sponsorship documents when required',
      'Translated / notarised / apostilled documents where applicable'
    ],


    visa: {
      type:
        'Long-stay / study residence process',

      processTime:
        'Processing time varies by application and documentation',

      validity:
        'Subject to immigration approval and renewal requirements',

      notes:
        'Students should use the latest university and Georgian immigration requirements because documentation and procedures can change.'
    },


    costOfLiving: {
      title:
        'Cost of Living in Georgia',

      intro:
        'Living expenses depend heavily on the city, accommodation type, food choices and personal spending.',

      points: [
        'Shared accommodation can reduce monthly expenses.',
        'Indian groceries and restaurants are available in major student areas.',
        'Public transport is commonly used by students in Tbilisi.',
        'Students should budget separately for accommodation, food, transport, insurance and personal expenses.'
      ]
    },


    lifestyle: {
      climate:
        'Four-season climate with warm summers and cold winters',

      language:
        'Georgian locally; medical programmes may be taught in English',

      food:
        'Indian restaurants, groceries and vegetarian options available in major student areas',

      community:
        'Established international and Indian student community in Tbilisi'
    },


    nmc: {
      title:
        'NMC and Indian Student Considerations',

      text:
        'Indian students planning to study medicine abroad should verify the latest National Medical Commission requirements, programme structure, internship requirements, licensing rules and eligibility conditions before taking admission. Regulations may change, so decisions should not be based only on older brochures or promotional material.'
    },


    faqs: [

      {
        q:
          'Is MBBS in Georgia taught in English?',

        a:
          'Several Georgian universities offer medical programmes in English for international students. Students should verify the exact language of instruction directly from the selected university.'
      },

      {
        q:
          'How long is MBBS in Georgia?',

        a:
          'Many medical programmes in Georgia are structured over approximately six years, although students should verify the exact curriculum and clinical training structure of their chosen university.'
      },

      {
        q:
          'Is NEET required for MBBS in Georgia?',

        a:
          'Indian students should consider NEET and current Indian regulatory requirements if they intend to maintain eligibility for medical registration or licensing pathways in India.'
      },

      {
        q:
          'How much does MBBS in Georgia cost?',

        a:
          'Costs vary significantly by university. Route Your Career currently lists selected Georgia options around the ₹20–28 lakh tuition range, excluding some living and associated expenses.'
      },

      {
        q:
          'Can Indian students get Indian food in Georgia?',

        a:
          'Indian restaurants, groceries and student mess options are available in major student areas, particularly in Tbilisi.'
      },

      {
        q:
          'Which university should I choose in Georgia?',

        a:
          'The right university depends on budget, curriculum, clinical exposure, location, teaching facilities and your future licensing plans. Students should compare universities rather than choosing purely on tuition fees.'
      }

    ]

  },


  /* =========================================================
     UZBEKISTAN — MBBS
  ========================================================= */

  uz: {

    seo: {
      title:
        'MBBS in Uzbekistan for Indian Students 2026 | Fees & Admission',

      description:
        'Explore MBBS in Uzbekistan for Indian students including fees, universities, eligibility, admission process, visa guidance and student life.',

      keywords: [
        'MBBS in Uzbekistan',
        'MBBS Uzbekistan for Indian students',
        'Uzbekistan MBBS fees',
        'medical universities Uzbekistan',
        'MBBS abroad'
      ]
    },


    overview: {
      title:
        'MBBS in Uzbekistan for Indian Students',

      intro:
        'Uzbekistan has become a growing destination for Indian medical students because of comparatively affordable tuition, government medical universities and increasing availability of English-medium programmes.'
    },


    highlights: [
      'Comparatively affordable medical education',
      'Government medical university options',
      'English-medium programmes available',
      'Growing Indian student population',
      'Shorter travel distance from India compared with many European destinations'
    ],


    eligibility: {
      title:
        'Eligibility for MBBS in Uzbekistan',

      points: [
        'Completion of Class 12 with Physics, Chemistry and Biology.',
        'Students should meet the admission criteria of the selected university.',
        'Indian students should verify current NEET and NMC requirements.',
        'Valid passport and supporting academic documents are required.'
      ]
    },


    academics: {
      duration:
        'Approximately 5–6 years depending on university',

      medium:
        'English programmes available',

      intake:
        'Typically annual intake',

      notes:
        'Exact programme duration, internship structure and academic requirements should be verified with the university.'
    },


    universities: [
      'Tashkent Medical Academy',
      'Samarkand State Medical University',
      'Andijan State Medical University',
      'Fergana Public Health Medical Institute'
    ],


    fees: {
      range:
        'Approx. ₹16–20 lakh tuition range across selected options',

      note:
        'Hostel, food, registration, visa, insurance, travel and administrative expenses should be calculated separately.'
    },


    admissionSteps: [
      'University selection',
      'Document eligibility review',
      'Application submission',
      'Admission confirmation',
      'Invitation / supporting documentation',
      'Visa processing',
      'Travel and university registration'
    ],


    documents: [
      'Passport',
      'Class 10 documents',
      'Class 12 documents',
      'NEET documents where applicable',
      'Photographs',
      'Medical documents',
      'University application documents',
      'Translated / notarised documents where required'
    ],


    visa: {
      type:
        'Student visa D',

      processTime:
        '2–4 weeks',

      validity:
        'Renewed yearly',

      notes:
        'Invitation letter from the university, plus medical and financial documentation may be required.'
    },


    costOfLiving: {
      title:
        'Living Costs in Uzbekistan',

      intro:
        'Uzbekistan is generally considered one of the more affordable MBBS-abroad destinations for day-to-day student expenses.',

      points: [
        'University hostel options are commonly available.',
        'Indian mess and vegetarian food options are available around many campuses.',
        'Daily transportation costs are generally manageable for students.'
      ]
    },


    lifestyle: {
      climate:
        'Continental — hot summers, cold winters',

      language:
        'Uzbek + Russian (English on campus)',

      food:
        'Muslim-majority — vegetarian & halal options widely available',

      community:
        'Large and growing Indian student community'
    },


    nmc: {
      title:
        'NMC Considerations',

      text:
        'Indian applicants should check current NMC regulations and confirm that the programme structure, clinical training and internship arrangements satisfy the requirements relevant to their future licensing pathway.'
    },


    faqs: [

      {
        q:
          'Is Uzbekistan affordable for MBBS?',

        a:
          'Uzbekistan is generally positioned among the more affordable international medical education destinations, although total cost depends on the university, hostel and living expenses.'
      },

      {
        q:
          'Is MBBS taught in English in Uzbekistan?',

        a:
          'English-medium programmes are available for international students at selected universities. Confirm the teaching medium before admission.'
      },

      {
        q:
          'Is NEET required?',

        a:
          'Indian students should verify current NEET and NMC requirements before enrolling in any foreign medical programme.'
      }

    ]

  },


  /* =========================================================
     IRELAND
  ========================================================= */

  ie: {

    visa: {
      type:
        'Stamp 2 student visa',

      processTime:
        '4–8 weeks',

      validity:
        'Duration of course',

      notes:
        'English-language requirements, financial proof and other immigration documentation may apply.'
    },

    lifestyle: {
      climate:
        'Mild Atlantic — rainy, cool summers',

      language:
        'English (Irish co-official)',

      food:
        'Cosmopolitan cities with Indian groceries and restaurants',

      community:
        'Vibrant Indian community in Dublin, Cork and Galway'
    }

  },


  /* =========================================================
     EGYPT
  ========================================================= */

  eg: {

    visa: {
      type:
        'Student visa',

      processTime:
        '2–4 weeks',

      validity:
        'Renewed yearly',

      notes:
        'University admission documentation and attested documents may be required.'
    },

    lifestyle: {
      climate:
        'Warm Mediterranean — mild winters',

      language:
        'Arabic locally; selected programmes may use English',

      food:
        'Vegetarian and halal options widely available',

      community:
        'International and Indian students in major university cities'
    }

  },


  /* =========================================================
     MOLDOVA
  ========================================================= */

  md: {

    visa: {
      type:
        'Long-stay student visa',

      processTime:
        '4–6 weeks',

      validity:
        'Renewed yearly',

      notes:
        'Invitation documentation, legalised academic documents and medical requirements may apply.'
    },

    lifestyle: {
      climate:
        'Cold winters, mild summers',

      language:
        'Romanian + Russian; English used in international programmes',

      food:
        'Vegetarian options available in major cities',

      community:
        'Smaller international and Indian student community'
    }

  },


  /* =========================================================
     RUSSIA
  ========================================================= */

  ru: {

    visa: {
      type:
        'Student visa — invitation based',

      processTime:
        'Approximately 3–4 weeks',

      validity:
        'Renewed according to study status',

      notes:
        'University invitation and additional immigration documentation are required.'
    },

    lifestyle: {
      climate:
        'Cold winters with significant regional variation',

      language:
        'Russian locally; some medical programmes offered in English',

      food:
        'Indian food and student mess options available around many international campuses',

      community:
        'Large Indian medical student population across multiple cities'
    }

  },


  /* =========================================================
     KAZAKHSTAN
  ========================================================= */

  kz: {

    visa: {
      type:
        'Student visa',

      processTime:
        'Approximately 3–4 weeks',

      validity:
        'Renewed according to study status',

      notes:
        'University invitation and medical documentation may be required.'
    },

    lifestyle: {
      climate:
        'Continental — hot summers and cold winters',

      language:
        'Kazakh + Russian; English programmes available',

      food:
        'Halal and vegetarian options available',

      community:
        'Growing Indian medical student community'
    }

  },


  /* =========================================================
     KYRGYZSTAN
  ========================================================= */

  kg: {

    visa: {
      type:
        'Student visa',

      processTime:
        'Approximately 2–3 weeks',

      validity:
        'Renewed according to course requirements',

      notes:
        'University invitation and immigration documentation are required.'
    },

    lifestyle: {
      climate:
        'Cold winters, mild summers',

      language:
        'Kyrgyz + Russian; English used in selected programmes',

      food:
        'Vegetarian and Indian food options around student areas',

      community:
        'Established Indian medical student population in Bishkek'
    }

  },


  /* =========================================================
     NEPAL
  ========================================================= */

  np: {

    visa: {
      type:
        'Special entry arrangements for Indian citizens',

      processTime:
        'Generally straightforward for Indian citizens',

      validity:
        'Subject to local study and registration requirements',

      notes:
        'Indian citizens have special travel arrangements with Nepal, but university registration and local documentation still apply.'
    },

    lifestyle: {
      climate:
        'Varies considerably by altitude and city',

      language:
        'Nepali with Hindi widely understood',

      food:
        'Indian and Nepalese food widely available',

      community:
        'Strong cultural and linguistic familiarity for Indian students'
    }

  },


  /* =========================================================
     USA — MANAGEMENT
  ========================================================= */

  us: {

    visa: {
      type:
        'F-1 student visa',

      processTime:
        'Varies by embassy appointment availability',

      validity:
        'Linked to programme and immigration status',

      notes:
        'I-20, SEVIS requirements, financial documentation and visa interview apply.'
    },

    lifestyle: {
      climate:
        'Highly regional',

      language:
        'English',

      food:
        'Indian food widely available in university cities',

      community:
        'Very large Indian student and professional community'
    }

  },


  /* =========================================================
     UNITED KINGDOM
  ========================================================= */

  gb: {

    visa: {
      type:
        'Student Route visa',

      processTime:
        'Processing times vary',

      validity:
        'Linked to course duration',

      notes:
        'CAS, financial evidence and other immigration requirements apply.'
    },

    lifestyle: {
      climate:
        'Mild and frequently rainy',

      language:
        'English',

      food:
        'Large Indian food and grocery network',

      community:
        'Very large Indian community'
    }

  },


  /* =========================================================
     AUSTRALIA
  ========================================================= */

  au: {

    visa: {
      type:
        'Subclass 500 student visa',

      processTime:
        'Processing times vary',

      validity:
        'Linked to course enrolment and visa conditions',

      notes:
        'Financial, enrolment, health insurance and other visa requirements apply.'
    },

    lifestyle: {
      climate:
        'Ranges from temperate to tropical depending on city',

      language:
        'English',

      food:
        'Indian restaurants and groceries widely available',

      community:
        'Large Indian student and migrant community'
    }

  },


  /* =========================================================
     GERMANY
  ========================================================= */

  de: {

    visa: {
      type:
        'National Type D student visa',

      processTime:
        'Can take several weeks or longer',

      validity:
        'Linked to study and residence status',

      notes:
        'APS, financial proof, health insurance and university admission documentation may apply.'
    },

    lifestyle: {
      climate:
        'Cool temperate climate',

      language:
        'German locally; many international programmes taught in English',

      food:
        'Indian restaurants and groceries widely available in university cities',

      community:
        'Large and growing Indian student community'
    }

  },


  /* =========================================================
     SPAIN
  ========================================================= */

  es: {

    visa: {
      type:
        'Long-stay student visa',

      processTime:
        'Processing times vary',

      validity:
        'Linked to programme duration',

      notes:
        'Residence registration and local documentation may be required after arrival.'
    },

    lifestyle: {
      climate:
        'Mediterranean in many regions',

      language:
        'Spanish locally; English programmes available',

      food:
        'Mediterranean cuisine and Indian restaurants in major cities',

      community:
        'Growing Indian student and professional community'
    }

  },


  /* =========================================================
     UAE
  ========================================================= */

  ae: {

    visa: {
      type:
        'Student residence visa',

      processTime:
        'Processing times vary',

      validity:
        'Usually linked to enrolment',

      notes:
        'University sponsorship and Emirates ID procedures generally apply.'
    },

    lifestyle: {
      climate:
        'Hot desert climate',

      language:
        'Arabic officially; English widely used',

      food:
        'Extensive Indian food and grocery availability',

      community:
        'One of the world’s largest overseas Indian communities'
    }

  },


  /* =========================================================
     SINGAPORE
  ========================================================= */

  sg: {

    visa: {
      type:
        'Student Pass',

      processTime:
        'Processing times vary',

      validity:
        'Linked to course duration',

      notes:
        'Admission and immigration approval are required before enrolment.'
    },

    lifestyle: {
      climate:
        'Tropical and warm year-round',

      language:
        'English widely used; Tamil is an official language',

      food:
        'Extensive Indian vegetarian and non-vegetarian options',

      community:
        'Long-established Indian community'
    }

  }

};
